import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField as MuiTextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Paper,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Badge as CertificationIcon,
} from '@mui/icons-material';
import { useTranslate, useDataProvider, useNotify, useRefresh } from 'react-admin';
import { DocumentUpload } from '../DocumentUpload';
import { FileViewerDialog } from '../FileViewerDialog';

interface DetailedCertificationStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  onClose: () => void;
  existingData?: any;
  driver?: any;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

export const DetailedCertificationStep: React.FC<DetailedCertificationStepProps> = ({
  onNext,
  onBack,
  onClose,
  existingData,
  driver,
  isFirstStep,
  isLastStep
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  // State for certification records and dialog
  const [certificationRecords, setCertificationRecords] = useState([]);
  const [certificationDialogOpen, setCertificationDialogOpen] = useState(false);
  const [editingCertification, setEditingCertification] = useState(null);

  // State for certification types
  const [certificationTypes, setCertificationTypes] = useState([]);
  const [certificationTypesLoading, setCertificationTypesLoading] = useState(false);

  // State for file viewer
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);

  // Form state for the dialog
  const [certificationForm, setCertificationForm] = useState({
    certificationTypeId: '',
    certificationTitle: '',
    certificationSummary: '',
    completedDate: '',
    expiryDate: '',
    hasDocument: false,
    documentFileId: null,
    documentFile: null
  });

  // Load certification records when driver is available
  useEffect(() => {
    if (driver?.certifications) {
      setCertificationRecords(driver.certifications);
    } else if (driver?.id) {
      loadCertificationRecords();
    }
  }, [driver?.id, driver?.certifications]);

  // Load certification types when component mounts
  useEffect(() => {
    const fetchCertificationTypes = async () => {
      setCertificationTypesLoading(true);
      try {
        const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
        const accessToken = localStorage.getItem('accessToken');
        const tenantsEnabled = import.meta.env.VITE_TENANTS_ENABLED === 'true';

        const response = await fetch(`${baseUrl}/code-tables/certification-types`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            ...(tenantsEnabled && { 'x-tenant-id': localStorage.getItem('tenantId') })
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch certification types`);
        }
        const data = await response.json();
        setCertificationTypes(data);
      } catch (error) {
        console.error('Error fetching certification types:', error);
        notify('Could not load certification types. Please contact support.', { type: 'warning' });
        setCertificationTypes([]);
      } finally {
        setCertificationTypesLoading(false);
      }
    };
    fetchCertificationTypes();
  }, []);

  const loadCertificationRecords = async () => {
    try {
      if (driver?.certifications) {
        setCertificationRecords(driver.certifications);
      } else if (driver?.id) {
        const result = await dataProvider.getOne('drivers', { id: driver.id });
        if (result.data?.certifications) {
          setCertificationRecords(result.data.certifications);
        }
      }
    } catch (error) {
      console.error('Error loading certification records:', error);
      setCertificationRecords([]);
    }
  };

  const handleEditCertification = (certification: any) => {
    setEditingCertification(certification);
    setCertificationForm({
      certificationTypeId: certification.certificationTypeId || '',
      certificationTitle: certification.certificationType?.name || certification.otherName || '',
      certificationSummary: certification.description || '',
      completedDate: certification.completedDate ? certification.completedDate.split('T')[0] : '',
      expiryDate: certification.expiryDate ? certification.expiryDate.split('T')[0] : '',
      hasDocument: !!certification.fileId,
      documentFileId: certification.fileId || null,
      documentFile: certification.file || null
    });
    setCertificationDialogOpen(true);
  };

  const handleDeleteCertification = async (certificationId: number) => {
    if (window.confirm('Are you sure you want to delete this certification?')) {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');
      const tenantsEnabled = import.meta.env.VITE_TENANTS_ENABLED === 'true';

      try {
        await dataProvider.delete('drivers/certifications', { id: certificationId });
        notify('Certification deleted successfully', { type: 'success' });
        loadCertificationRecords();
        // Refresh the page to ensure data is updated
        window.location.reload();
      } catch (error) {
        console.error('Error deleting certification:', error);
        notify('Failed to delete certification', { type: 'error' });
      }
    }
  };

  const openFileViewer = (file: any) => {
    setViewingFile(file);
    setFileViewerOpen(true);
  };

  const resetCertificationForm = () => {
    setCertificationForm({
      certificationTypeId: '',
      certificationTitle: '',
      certificationSummary: '',
      completedDate: '',
      expiryDate: '',
      hasDocument: false,
      documentFileId: null,
      documentFile: null
    });
    setEditingCertification(null);
  };

  const handleSaveCertification = async () => {
    try {
      if (editingCertification) {
        await dataProvider.update('drivers/certifications', {
          id: editingCertification.id,
          data: {
            certificationTypeId: certificationForm.certificationTypeId,
            certificationTitle: certificationForm.certificationTitle,
            certificationSummary: certificationForm.certificationSummary,
            completedDate: certificationForm.completedDate || null,
            expiryDate: certificationForm.expiryDate || null,
            status: 'ACTIVE',
            fileId: certificationForm.documentFileId
          }
        });
        notify('Certification updated successfully', { type: 'success' });
      } else {
        await dataProvider.create('drivers/certifications', {
          data: {
            certificationTypeId: certificationForm.certificationTypeId,
            certificationTitle: certificationForm.certificationTitle,
            certificationSummary: certificationForm.certificationSummary,
            completedDate: certificationForm.completedDate || null,
            expiryDate: certificationForm.expiryDate || null,
            status: 'ACTIVE',
            driverId: driver.id,
            fileId: certificationForm.documentFileId
          }
        });
        notify('Certification added successfully', { type: 'success' });
      }
      setCertificationDialogOpen(false);
      resetCertificationForm();
      loadCertificationRecords();
      // Refresh the page to ensure data is updated
      window.location.reload();
    } catch (error) {
      notify(`Failed to ${editingCertification ? 'update' : 'add'} certification`, { type: 'error' });
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 'normal', mb: 4, textAlign: 'center' }}>
        <CertificationIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
        Certifications
      </Typography>

      {/* Certification Records Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Certification Records</Typography>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => {
              resetCertificationForm();
              setCertificationDialogOpen(true);
            }}
          >
            Add Certification
          </Button>
        </Box>

        {certificationRecords.length ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Certification Type</TableCell>
                <TableCell>Completed Date</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Document</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {certificationRecords.map((cert: any) => {
                const isExpired = cert.expiryDate && new Date(cert.expiryDate) < new Date();
                return (
                  <TableRow key={cert.id}>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {cert.certificationType?.name || cert.otherName || 'Unknown Certification'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {cert.completedDate ? new Date(cert.completedDate).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {cert.expiryDate ? new Date(cert.expiryDate).toLocaleDateString() : 'No expiry'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={isExpired ? 'Expired' : 'Active'}
                        color={isExpired ? 'error' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {cert.file ? (
                        <Button
                          size="small"
                          onClick={() => openFileViewer(cert.file)}
                          variant="outlined"
                        >
                          View Document
                        </Button>
                      ) : (
                        'No document'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEditCertification(cert)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteCertification(cert.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No certification records found. Click "Add Certification" to get started.
          </Typography>
        )}
      </Paper>

      {/* Action Buttons */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: 4
      }}>
        {!isFirstStep && (
          <Button
            variant="outlined"
            size="large"
            onClick={onBack}
            sx={{ px: 4, py: 1.5 }}
          >
            Back
          </Button>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant="contained"
          size="large"
          onClick={() => onNext({})}
          sx={{ px: 4, py: 1.5 }}
        >
          {isLastStep ? 'Complete' : 'Next'}
        </Button>
      </Box>

      {/* Add/Edit Certification Dialog */}
      <Dialog
        open={certificationDialogOpen}
        onClose={() => setCertificationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingCertification ? 'Edit Certification' : 'Add Certification'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="body2" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                  Certification Title
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Certification</InputLabel>
                  <Select
                    value={certificationForm.certificationTypeId}
                    onChange={(e) => {
                      const selectedType = certificationTypes.find((type: any) => type.id === e.target.value);
                      setCertificationForm({
                        ...certificationForm,
                        certificationTypeId: e.target.value,
                        certificationTitle: selectedType?.name || ''
                      });
                    }}
                    label="Select Certification"
                    disabled={certificationTypesLoading}
                  >
                    {certificationTypesLoading && (
                      <MenuItem disabled>Loading certification types...</MenuItem>
                    )}
                    {certificationTypes.map((type: any) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                  Certification Summary
                </Typography>
                <MuiTextField
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Enter Certification Summary"
                  value={certificationForm.certificationSummary}
                  onChange={(e) => setCertificationForm({
                    ...certificationForm,
                    certificationSummary: e.target.value
                  })}
                />
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                  Certification Completed (Optional)
                </Typography>
                <MuiTextField
                  label="Completed Date (Optional)"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={certificationForm.completedDate}
                  onChange={(e) => setCertificationForm({
                    ...certificationForm,
                    completedDate: e.target.value
                  })}
                />
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                  Certification Expiry (Optional)
                </Typography>
                <MuiTextField
                  label="Expiry Date (Optional)"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={certificationForm.expiryDate}
                  onChange={(e) => setCertificationForm({
                    ...certificationForm,
                    expiryDate: e.target.value
                  })}
                />
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                  Certification Document (Optional)
                </Typography>

                {/* Show existing file if editing */}
                {editingCertification && certificationForm.documentFile && (
                  <Box sx={{
                    mb: 2,
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    backgroundColor: '#f9f9f9'
                  }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => {
                            if (certificationForm.documentFile) {
                              openFileViewer(certificationForm.documentFile);
                            }
                          }}
                        >
                          {certificationForm.documentFile.name || 'Certification Document'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Current file (click to view)
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          setCertificationForm({
                            ...certificationForm,
                            documentFileId: null,
                            documentFile: null,
                            hasDocument: false
                          });
                        }}
                      >
                        Remove
                      </Button>
                    </Stack>
                  </Box>
                )}

                <DocumentUpload
                  documentType="certification"
                  driverId={driver?.id}
                  onUploadSuccess={(fileData) => {
                    setCertificationForm({
                      ...certificationForm,
                      documentFileId: fileData.id,
                      documentFile: fileData,
                      hasDocument: true
                    });
                    notify('Certification document uploaded successfully', { type: 'success' });
                  }}
                  onUploadError={(error) => {
                    notify(`Upload failed: ${error}`, { type: 'error' });
                  }}
                  acceptTypes="image/*,.pdf,.doc,.docx"
                  maxSizeMB={10}
                />
              </Box>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setCertificationDialogOpen(false)}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            Close
          </Button>
          {editingCertification && (
            <Button
              onClick={() => {
                handleDeleteCertification(editingCertification.id);
                setCertificationDialogOpen(false);
                resetCertificationForm();
              }}
              color="error"
              startIcon={<DeleteIcon />}
              sx={{ minWidth: 100 }}
            >
              Delete
            </Button>
          )}
          <Button
            disabled={!certificationForm.certificationTypeId}
            onClick={handleSaveCertification}
            variant="contained"
            sx={{ minWidth: 100 }}
          >
            {editingCertification ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Viewer Dialog */}
      <FileViewerDialog
        open={fileViewerOpen}
        onClose={() => setFileViewerOpen(false)}
        file={viewingFile}
      />
    </Box>
  );
};